/**
 * Avatar gallery cache — fetches the list of pre-generated avatars from
 * `/api/avatars` (optionally filtered by gender) once per page load and
 * resolves a deterministic fallback for any user that has neither uploaded
 * a profile picture nor explicitly chosen one from the gallery.
 *
 * Stable cross-session: the same `userId` always maps to the same avatar
 * (djb2(userId) % pool.length) so users never "flicker" between avatars.
 *
 * The backend returns CloudFlare Images URLs (`cdn_url`) when available.
 * We prefer those so browsers hit a global edge instead of the pod, and
 * pass them through `cfResize()` to request the right pixel size for the
 * surface (e.g. 64×64 for an avatar thumb, 256 for a picker tile).
 */
import { cfResize } from './cfImage';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Keyed by gender filter (`""`, `"male"`, `"female"`) so we cache each pool.
const _galleryPromises = {};
const _galleries = {};

function _fetchGallery(gender = '') {
  if (_galleryPromises[gender]) return _galleryPromises[gender];
  const url = gender ? `${API}/avatars?gender=${gender}` : `${API}/avatars`;
  _galleryPromises[gender] = fetch(url)
    .then((r) => (r.ok ? r.json() : { avatars: [] }))
    .then((d) => {
      _galleries[gender] = Array.isArray(d?.avatars) ? d.avatars : [];
      return _galleries[gender];
    })
    .catch(() => {
      _galleries[gender] = [];
      return _galleries[gender];
    });
  return _galleryPromises[gender];
}

/** Lazily warm one or more gallery pools. */
export function preloadAvatarGallery(gender = '') {
  return _fetchGallery(gender);
}

/** Synchronous accessor for the currently cached pool. */
export function getCachedGallery(gender = '') {
  return _galleries[gender] || [];
}

function _hash(seed = '') {
  let h = 5381;
  const s = String(seed || '');
  for (let i = 0; i < s.length; i++) h = ((h << 5) + h + s.charCodeAt(i)) | 0;
  return Math.abs(h);
}

/**
 * Pick the best URL we have for an avatar entry — prefer the CloudFlare
 * delivery URL, fall back to the on-pod file route, and finally to the
 * absolute backend URL.
 */
function _urlFor(entry) {
  if (!entry) return null;
  if (entry.cdn_url) return entry.cdn_url;
  if (entry.url && entry.url.startsWith('http')) return entry.url;
  if (entry.url) return `${BACKEND_URL}${entry.url}`;
  if (entry.fallback_url) return `${BACKEND_URL}${entry.fallback_url}`;
  return null;
}

/**
 * Resolve the avatar URL to show for a user.
 *
 * Priority:
 *   1. `chosenAvatarId` (user picked one from the gallery / styled it)
 *   2. deterministic gallery pick based on `userId` + `gender`
 *   3. null (caller falls back to initials)
 *
 * `size` (px) — when set, the CF URL is rewritten to request a resize
 *               (saves ~80% bandwidth for small thumbs). No-op for
 *               non-CF URLs.
 */
export function resolveGalleryAvatarUrl({ chosenAvatarId, userId, gender, size }) {
  let raw = null;
  if (chosenAvatarId) {
    if (chosenAvatarId.startsWith('styled_')) {
      raw = `${API}/avatars/file/${chosenAvatarId}.png`;
    } else {
      const pools = Object.values(_galleries).flat();
      const entry = pools.find((a) => a && a.id === chosenAvatarId);
      raw = entry ? _urlFor(entry) : `${API}/avatars/file/${chosenAvatarId}.png`;
    }
  } else if (userId) {
    let pool = [];
    if (gender === 'male' || gender === 'female') {
      pool = _galleries[gender] || [];
      if (pool.length === 0) {
        _fetchGallery(gender);
        pool = _galleries[''] || [];
      }
    } else {
      pool = _galleries[''] || [];
    }
    if (pool.length) {
      const idx = _hash(userId) % pool.length;
      raw = _urlFor(pool[idx]);
    }
  }
  if (!raw) return null;
  // Pick a sensible variant for the requested render size. Round up to
  // the nearest power-of-2-ish bucket so the CDN cache key stays small.
  if (size) {
    const target =
      size <= 64 ? 64 :
      size <= 128 ? 128 :
      size <= 256 ? 256 :
      size <= 512 ? 512 :
      1024;
    return cfResize(raw, { w: target, h: target });
  }
  return raw;
}

/** Avatar id for a userId — used by the picker to highlight the
 * "current fallback" tile when nothing's been chosen. */
export function deterministicAvatarId(userId, gender = '') {
  const pool = _galleries[gender === 'male' || gender === 'female' ? gender : ''] || [];
  if (!pool.length || !userId) return null;
  const idx = _hash(userId) % pool.length;
  return pool[idx]?.id || null;
}
